import ControlledForm from "../components/form/controlled-form.tsx";
import ControlledInput from "../components/form/controlled-input.tsx";
import { useFormContext } from "react-hook-form";
import { useEffect } from "react";
import { Button } from "../components/ui/button.tsx";
import { IoCheckmark } from "react-icons/io5";
import { FaTrashAlt } from "react-icons/fa";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { CreateCodebookEntryRequest } from "@ns/api";
import { toaster } from "../components/ui/toaster.tsx";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../api/custom-api.ts";
import {
  CreateWarehouseEntry,
  createWarehouseEntrySchema,
} from "../schemas/create-entry.schema.ts";
import { formatDate } from "../utils/mappers/date.mapper.ts";
import ControlledDatePicker from "../components/form/controlled-date-input.tsx";
import ControlledTextArea from "../components/form/controlled-textarea.tsx";

const CreateCodebookEntryForm = () => {
  const {
    handleSubmit,
    formState: { isValid },
    reset,
  } = useFormContext<CreateWarehouseEntry>();

  const navigate = useNavigate();

  const resetFields = () => {
    reset({
      note: "",
      documentNumber: "",
      supplierName: "",
      origin: "",
      purchaseDate: undefined,
      purchasePrice: "",
      quantity: "",
    });
  };

  useEffect(() => {
    resetFields();
  }, []);

  const { id } = useParams();

  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    mutationFn: (request: CreateCodebookEntryRequest) =>
      api.codebookEntry.createCodebookEntry(request),
    onSuccess: () => {
      toaster.create({
        title: "Kreiran novi nalog!",
        // @ts-expect-error
        placement: "bottom-end",
        description: "Novi nalog je uspešno kreiran",
        type: "info",
        duration: 5000,
      });
      resetFields();
      queryClient.invalidateQueries({ queryKey: ["entries", id] });
      navigate(`/warehouses/${id}/entries`);
    },
    onError: () => {
      toaster.create({
        title: "Greška!",
        // @ts-expect-error
        placement: "bottom-end",
        description: "Proverite unete podatke",
        type: "error",
        duration: 5000,
      });
    },
  });

  const onSubmit = handleSubmit((data) => {
    mutate({
      codebookId: id!,
      documentNumber: data.documentNumber,
      supplierName: data.supplierName,
      origin: data.origin,
      // @ts-ignore
      purchaseDate: data?.purchaseDate
        ? formatDate(data.purchaseDate)
        : undefined,
      purchasePrice: data.purchasePrice,
      note: data.note,
      quantity: Number(data.quantity),
    });
  });

  return (
    <div className="pt-[5.25rem] h-full xl:h-full p-[1rem] bg-[#ffffff] flex flex-col items-center">
      <div className="w-full flex flex-col sm:flex-row justify-between items-center px-[0.25rem]">
        <div className="flex flex-row gap-[1rem] items-center">
          <span className={"text-[24px] text-black font-semibold"}>
            KREIRAJ NOVI ULAZ MATERIJALA
          </span>
        </div>
        <div className="flex flex-row gap-[1rem] items-center">
          <div className="w-[10rem] sm:w-[12rem]">
            <Button
              size={"sm"}
              colorPalette={"black"}
              variant={"surface"}
              width={"full"}
              color={"#000000"}
              backgroundColor={"#ffffff"}
              borderColor={"#ffffff"}
              fontWeight={"600"}
              fontSize={"14px"}
              borderRadius={"0.5rem"}
              disabled={!isValid}
              onClick={onSubmit}
            >
              <IoCheckmark /> Potvrdi
            </Button>
          </div>
          <div className="w-[10rem] sm:w-[12rem]">
            <Button
              size={"sm"}
              colorPalette={"black"}
              variant={"surface"}
              width={"full"}
              color={"#000000"}
              backgroundColor={"#ffffff"}
              borderColor={"#ffffff"}
              fontWeight={"600"}
              fontSize={"14px"}
              borderRadius={"0.5rem"}
              onClick={() => resetFields()}
            >
              <FaTrashAlt /> Poništi
            </Button>
          </div>
        </div>
      </div>
      <div className="w-full h-full flex flex-col gap-[1rem] mt-[1rem] items-center overflow-scroll">
        <div className="w-full flex flex-col items-center gap-[1rem] justify-between mt-[1rem] px-[0.25rem]">
          <ControlledInput
            className="w-full sm:w-[24rem] md:w-[28rem]"
            name={"documentNumber"}
            placeholder={"Unesi broj fakutre"}
            label={"Broj fakture"}
          />
          <ControlledInput
            name={"supplierName"}
            className="w-full sm:w-[24rem] md:w-[28rem]"
            label={"Ime dobavljaca"}
            placeholder={"Unesi ime dobavljaca"}
          />
          <ControlledInput
            name={"origin"}
            className="w-full sm:w-[24rem] md:w-[28rem]"
            label={"Poreklo materijala"}
            placeholder={"Unesi poreklo materijala"}
          />
          <ControlledDatePicker
            name={"purchaseDate"}
            className="w-full sm:w-[24rem] md:w-[28rem]"
            label={"Datum uvoza"}
            placeholder={"Unesi datum uvoza (dd/mm/yyyy)"}
          />
          <ControlledInput
            name={"purchasePrice"}
            className="w-full sm:w-[24rem] md:w-[28rem]"
            label={"Cenu po fakturi (EUR)"}
            placeholder={"Unesi cenu po fakturi (EUR)"}
          />
          <ControlledInput
            name={"quantity"}
            className="w-full sm:w-[24rem] md:w-[28rem]"
            label={"Kolicina materijala"}
            placeholder={"Unesi kolicinu materijala"}
          />
          <ControlledTextArea
            name={"note"}
            className="w-full sm:w-[24rem] md:w-[28rem] h-[8rem] md:h-[12rem]"
            label={"Napomena"}
            size={"lg"}
            placeholder={"Unesi napomenu"}
          />
        </div>
      </div>
    </div>
  );
};

export const CreateCodebookEntryPage = () => {
  return (
    <ControlledForm schema={createWarehouseEntrySchema}>
      <CreateCodebookEntryForm />
    </ControlledForm>
  );
};
